import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import EigooMark from "../components/EigooMark";
import {
    RefreshCw,
    Search,
    Mail,
    Building2,
    Tag,
    MessageSquare,
    TrendingUp,
    Users,
    Send,
    AlertCircle,
} from "lucide-react";

/**
 * DirectorLeads — Director-only inbox for BIaaS Intake Form submissions.
 * Renders leads (newest first) plus the pricing-view → form-submit funnel
 * so the Director can triage before HubSpot is fully wired.
 */
const GOLD = "#C9A84C";

const HS_STATUS_STYLE = {
    created: { color: "#68D391", label: "SYNCED" },
    duplicate: { color: "#68D391", label: "SYNCED" },
    skipped: { color: "#F6C560", label: "LOCAL ONLY" },
    error: { color: "#FC8181", label: "HUBSPOT ERR" },
    rejected: { color: "#FC8181", label: "REJECTED" },
    unknown: { color: "#829AB1", label: "—" },
};

function StatPill({ icon: Icon, label, value, accent }) {
    return (
        <div
            className="p-5 rounded-[10px]"
            style={{
                background: "#0A0A0A",
                border: `1px solid rgba(201,168,76,0.14)`,
                borderLeft: `3px solid ${accent || GOLD}`,
            }}
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                <Icon size={12} style={{ color: accent || GOLD }} />
                <span>{label}</span>
            </div>
            <div
                className="text-3xl"
                style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    color: "#EDEDED",
                    letterSpacing: "-0.02em",
                }}
            >
                {value}
            </div>
        </div>
    );
}

function LeadRow({ lead }) {
    const hs = HS_STATUS_STYLE[lead.hubspot_status] || HS_STATUS_STYLE.unknown;
    const when = lead.created_at
        ? new Date(lead.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
          })
        : "—";
    return (
        <tr
            data-testid={`lead-row-${lead.id}`}
            className="border-t transition-colors"
            style={{ borderColor: "rgba(201,168,76,0.08)" }}
            onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(201,168,76,0.03)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
            <td className="px-4 py-3 align-top text-xs" style={{ color: "#C8C8C8" }}>
                {when}
            </td>
            <td className="px-4 py-3 align-top">
                <div style={{ color: "#EDEDED", fontWeight: 600 }}>{lead.full_name}</div>
                <a
                    href={`mailto:${lead.email}`}
                    className="text-xs flex items-center gap-1.5 mt-1"
                    style={{ color: GOLD }}
                >
                    <Mail size={11} /> {lead.email}
                </a>
            </td>
            <td className="px-4 py-3 align-top">
                <div className="flex items-center gap-1.5 text-xs" style={{ color: "#C8C8C8" }}>
                    <Building2 size={11} style={{ color: GOLD }} /> {lead.business_sector}
                </div>
                {lead.preferred_tier && (
                    <div
                        className="text-[10px] uppercase tracking-[0.22em] mt-1"
                        style={{ color: "#829AB1" }}
                    >
                        <Tag size={9} style={{ display: "inline", marginRight: 4 }} />
                        {lead.preferred_tier}
                    </div>
                )}
            </td>
            <td className="px-4 py-3 align-top text-xs max-w-[320px]" style={{ color: "#A8A8A8" }}>
                {lead.primary_challenge ? (
                    <div className="flex gap-1.5">
                        <MessageSquare size={11} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
                        <span>{lead.primary_challenge}</span>
                    </div>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <span
                    className="inline-flex text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded"
                    style={{
                        color: hs.color,
                        background: `${hs.color}12`,
                        border: `1px solid ${hs.color}40`,
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                    }}
                    data-testid={`lead-hubspot-${lead.id}`}
                >
                    {hs.label}
                </span>
            </td>
        </tr>
    );
}

export default function DirectorLeads() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [sectorFilter, setSectorFilter] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/hubspot-lead", { params: { limit: 200 } });
            setData(r.data);
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                    "Unable to load leads. Confirm you are signed in as Director."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const sectors = useMemo(() => {
        if (!data?.leads) return [];
        return [...new Set(data.leads.map((l) => l.business_sector).filter(Boolean))].sort();
    }, [data]);

    const filtered = useMemo(() => {
        if (!data?.leads) return [];
        const needle = q.trim().toLowerCase();
        return data.leads.filter((l) => {
            if (sectorFilter && l.business_sector !== sectorFilter) return false;
            if (!needle) return true;
            return (
                (l.full_name || "").toLowerCase().includes(needle) ||
                (l.email || "").toLowerCase().includes(needle) ||
                (l.primary_challenge || "").toLowerCase().includes(needle) ||
                (l.preferred_tier || "").toLowerCase().includes(needle)
            );
        });
    }, [data, q, sectorFilter]);

    const funnel = data?.funnel || {
        pricing_views: 0,
        form_submits: 0,
        form_errors: 0,
        conversion_pct: 0,
    };

    return (
        <div className="min-h-screen" style={{ background: "#050505" }}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <EigooMark size="md" sub="Director · Lead Inbox" testid="leads-mark" />
                        <h1
                            className="mt-4 text-3xl md:text-4xl"
                            style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: "#EDEDED",
                                letterSpacing: "-0.02em",
                            }}
                            data-testid="leads-title"
                        >
                            BIaaS Intake · Lead Inbox
                        </h1>
                        <p
                            className="mt-2 text-sm"
                            style={{ color: "#A8A8A8", fontFamily: "'Inter', sans-serif" }}
                        >
                            Every "Send My Signal" submission lands here. Triage before HubSpot sync.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="leads-refresh"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs uppercase tracking-[0.2em] transition-all"
                        style={{
                            background: "transparent",
                            border: `1px solid ${GOLD}`,
                            color: GOLD,
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            opacity: loading ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (!loading) e.currentTarget.style.background = "rgba(201,168,76,0.08)";
                        }}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                        {loading ? "Loading" : "Refresh"}
                    </button>
                </div>

                {/* Funnel + totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="leads-funnel">
                    <StatPill
                        icon={Users}
                        label="Total Leads"
                        value={data?.total_leads ?? 0}
                    />
                    <StatPill
                        icon={TrendingUp}
                        label="Pricing Views"
                        value={funnel.pricing_views}
                        accent="#22D3EE"
                    />
                    <StatPill
                        icon={Send}
                        label="Form Submits"
                        value={funnel.form_submits}
                    />
                    <StatPill
                        icon={TrendingUp}
                        label="Conversion"
                        value={`${funnel.conversion_pct}%`}
                        accent="#68D391"
                    />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <label
                        className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 min-w-[220px]"
                        style={{
                            background: "#0A0A0A",
                            border: "1px solid rgba(201,168,76,0.18)",
                        }}
                    >
                        <Search size={14} style={{ color: GOLD }} />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, email, challenge, tier..."
                            data-testid="leads-search"
                            className="flex-1 bg-transparent text-sm outline-none"
                            style={{ color: "#EDEDED" }}
                        />
                    </label>
                    <select
                        value={sectorFilter}
                        onChange={(e) => setSectorFilter(e.target.value)}
                        data-testid="leads-sector-filter"
                        className="px-3 py-2 rounded-md text-sm"
                        style={{
                            background: "#0A0A0A",
                            border: "1px solid rgba(201,168,76,0.18)",
                            color: "#EDEDED",
                            minWidth: 180,
                        }}
                    >
                        <option value="">All sectors</option>
                        {sectors.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Error */}
                {err && (
                    <div
                        data-testid="leads-error"
                        className="flex items-start gap-2 p-4 rounded-md mb-6 text-sm"
                        style={{
                            background: "rgba(255,100,100,0.06)",
                            border: "1px solid rgba(255,100,100,0.25)",
                            color: "#F8B4B4",
                        }}
                    >
                        <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{err}</span>
                    </div>
                )}

                {/* Table */}
                <div
                    className="rounded-[10px] overflow-hidden"
                    style={{
                        background: "#0A0A0A",
                        border: "1px solid rgba(201,168,76,0.14)",
                    }}
                    data-testid="leads-table-wrap"
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr>
                                {["When", "Contact", "Sector / Tier", "Challenge", "HubSpot"].map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.25em]"
                                        style={{
                                            color: GOLD,
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            background: "rgba(201,168,76,0.04)",
                                        }}
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !data ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="px-4 py-10 text-center text-sm text-muted-foreground"
                                    >
                                        Loading leads...
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        data-testid="leads-empty"
                                        className="px-4 py-10 text-center text-sm text-muted-foreground"
                                    >
                                        {data?.total_leads
                                            ? "No leads match the current filters."
                                            : "No signals yet. As soon as the first \"Send My Signal\" fires, it lands here."}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((lead) => <LeadRow key={lead.id} lead={lead} />)
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 text-xs text-muted-foreground" data-testid="leads-count">
                    {filtered.length} shown · {data?.total_leads ?? 0} total ·{" "}
                    {data?.hubspot_synced ?? 0} synced to HubSpot
                </div>
            </div>
        </div>
    );
}
